"use client";

import { motion } from "framer-motion";
import { ArrowRight, Code2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import { BgPatternOverlay } from "@/app/(marketing)/_components/bg-pattern-overlay";
import { Container } from "@/app/(marketing)/_components/ui/container";

const steps = [
  {
    number: "01",
    title: "Apply",
    description: "Create your profile and tell us about your expertise.",
  },
  {
    number: "02",
    title: "Get matched",
    description: "We connect you with projects that match your background.",
  },
  {
    number: "03",
    title: "Start contributing",
    description: "Complete tasks and collaborate with researchers.",
  },
  {
    number: "04",
    title: "Get paid",
    description: "Once the project is completed, you'll receive the payment.",
  },
];

export default function ExpertsHowItWorks() {
  return (
    <section className="py-4 lg:py-6">
      <div className="bg-marketing-blue relative mx-4 overflow-hidden rounded-[1.25rem]">
        <div
          className="pointer-events-none absolute inset-0 z-0 mix-blend-color-dodge select-none"
          aria-hidden="true"
        >
          <Image
            src="/marketing/backgrounds/white-texture.jpg"
            alt=""
            fill
            className="object-cover"
            draggable={false}
          />
        </div>

        <BgPatternOverlay
          tileCount={1}
          className="absolute inset-0 z-0 overflow-hidden opacity-40"
        />

        <div className="relative z-10 py-12 lg:py-16">
          <Container>
            <div className="mb-8 flex flex-col items-start gap-4">
              <div className="rounded-full border border-white/15 bg-white/10 px-3 py-1 backdrop-blur-sm">
                <p className="font-mono text-sm tracking-[-0.28px] text-white/90">
                  How it works
                </p>
              </div>
              <h2 className="max-w-[600px] text-3xl leading-[1.1] font-medium tracking-[-1.5px] text-white md:text-4xl lg:text-[50px]">
                Four steps to start contributing
              </h2>
              <p className="max-w-[500px] text-lg font-medium text-white/80">
                From application to payout, here&apos;s the journey every Terac
                expert takes.
              </p>
            </div>

            <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_minmax(360px,1fr)] lg:gap-12">
              <div>
                <ol className="border-t border-white/15">
                  {steps.map((step, index) => (
                    <motion.li
                      key={step.number}
                      initial={{ opacity: 0, transform: "translateY(20px)" }}
                      whileInView={{ opacity: 1, transform: "translateY(0px)" }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex gap-6 border-b border-white/15 py-4 lg:py-5"
                    >
                      <span className="font-mono text-lg font-medium text-white/50">
                        {step.number}
                      </span>
                      <div>
                        <h3 className="mb-2 text-xl font-medium text-white">
                          {step.title}
                        </h3>
                        <p className="text-base leading-relaxed text-white/70">
                          {step.description}
                        </p>
                      </div>
                    </motion.li>
                  ))}
                </ol>

                <div className="mt-6">
                  <Link
                    href="/login"
                    className="group inline-flex items-center gap-2 text-base font-medium text-white"
                  >
                    <span>Apply now</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>

              <HowItWorksVisual />
            </div>
          </Container>
        </div>
      </div>
    </section>
  );
}

const BARCODE_BARS = [
  2, 1, 3, 1, 2, 1, 1, 3, 2, 1, 3, 2, 1, 1, 2, 3, 1, 2, 1, 3, 2, 1, 2, 1, 3, 1,
  2, 3, 1, 2, 1, 3, 1, 2, 1, 3, 2, 1, 3, 1, 2, 1, 3, 2, 1, 2, 1, 3, 2, 1,
];

function HowItWorksVisual() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      setMouse({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <div className="relative flex min-h-[340px] items-center justify-center lg:min-h-[380px]">
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes howItWorksFloat1 {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            @keyframes howItWorksFloat2 {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-12px); }
            }
            @keyframes howItWorksFloat3 {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-8px); }
            }
          `,
        }}
      />

      <MiniBadge />

      <div
        aria-hidden
        className="pointer-events-none absolute top-2 right-0 hidden lg:block"
        style={{
          transform: `translate(${mouse.x * -9}px, ${mouse.y * -7}px)`,
          transition: "transform 0.2s ease-out",
        }}
      >
        <div style={{ animation: "howItWorksFloat1 3s ease-in-out infinite" }}>
          <MatchedTile />
        </div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute bottom-6 left-0 hidden lg:block"
        style={{
          transform: `translate(${mouse.x * 12}px, ${mouse.y * 9}px)`,
          transition: "transform 0.2s ease-out",
        }}
      >
        <div
          style={{
            animation: "howItWorksFloat2 3.5s ease-in-out infinite 0.5s",
          }}
        >
          <SubmissionTile />
        </div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute right-2 bottom-2 hidden lg:block"
        style={{
          transform: `translate(${mouse.x * -11}px, ${mouse.y * -12}px)`,
          transition: "transform 0.2s ease-out",
        }}
      >
        <div
          style={{ animation: "howItWorksFloat3 2.8s ease-in-out infinite 1s" }}
        >
          <PayoutTile />
        </div>
      </div>
    </div>
  );
}

function MiniBadge() {
  return (
    <div
      className="border-marketing-border bg-marketing-cream relative w-[200px] overflow-hidden rounded-[14px] border"
      role="img"
      aria-label="Sample expert profile"
    >
      <div className="bg-marketing-blue flex items-center justify-between px-3 py-1.5">
        <span className="text-marketing-cream font-mono text-[9px] tracking-[0.22em] uppercase">
          Expert
        </span>
        <div className="flex items-center gap-1">
          <div
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: "var(--color-marketing-accent-green-dark)" }}
          />
          <span className="text-marketing-cream/90 font-mono text-[8.5px] tracking-wider uppercase">
            Tier 1
          </span>
        </div>
      </div>

      <div className="flex flex-col items-center px-3 pt-3 pb-2">
        <div className="border-marketing-border bg-marketing-cream-light relative h-[80px] w-[80px] rounded-full border-2">
          <div className="absolute inset-0 overflow-hidden rounded-full">
            <Image
              src="/marketing/experts/expert-9.png"
              alt=""
              fill
              sizes="80px"
              className="object-cover"
              draggable={false}
            />
          </div>
          <div className="bg-marketing-blue border-marketing-cream absolute -right-1 -bottom-1 flex h-[26px] w-[26px] items-center justify-center rounded-full border-[3px]">
            <Code2 size={13} className="text-marketing-cream" />
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-0.5 px-3 pb-1">
        <div className="flex items-center gap-1.5">
          <span className="text-foreground font-mono text-[11px] font-semibold tracking-[-0.02em]">
            J. Miller
          </span>
          <span className="text-[12px] leading-none" aria-label="US">
            🇺🇸
          </span>
        </div>
        <span className="text-marketing-text-muted/60 font-mono text-[8.5px] tracking-[0.05em] uppercase">
          Sr. Software Engineer
        </span>
      </div>

      <div className="border-marketing-border-light mx-3 mt-1.5 mb-2 grid grid-cols-3 gap-1 rounded-[6px] border bg-white/40 px-1 py-1.5">
        <Stat label="YRS" value="7" />
        <Stat label="STUDIES" value="134" />
        <Stat label="RATE" value="$145" />
      </div>

      <div className="flex items-center justify-center gap-1 px-3 pb-2">
        {["ID", "LI", "EM", "IP"].map((label) => (
          <div
            key={label}
            className="border-marketing-border bg-marketing-cream-light flex h-[16px] items-center justify-center gap-0.5 rounded-full border px-1.5"
          >
            <CheckGlyph />
            <span className="text-marketing-text-secondary font-mono text-[7px] tracking-wider">
              {label}
            </span>
          </div>
        ))}
      </div>

      <div className="border-marketing-border-light flex items-end gap-[1.5px] border-t px-3 py-1.5">
        {BARCODE_BARS.map((w, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`bar-${i}`}
            className="bg-marketing-text-muted/80"
            style={{ width: `${w}px`, height: "12px" }}
          />
        ))}
      </div>

      <div className="border-marketing-border-light bg-marketing-cream-light flex items-center justify-between border-t px-3 py-1.5">
        <div className="flex items-center gap-1">
          <Image
            src="/brand/logo.svg"
            alt=""
            width={10}
            height={10}
            draggable={false}
          />
          <span className="text-marketing-text-muted font-mono text-[8px] tracking-[0.18em] uppercase">
            Terac
          </span>
        </div>
        <span className="text-marketing-text-secondary font-mono text-[7.5px] tracking-[0.12em]">
          TR-B22F-4501
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-foreground font-mono text-[11px] leading-none font-semibold">
        {value}
      </span>
      <span className="text-marketing-text-muted/70 mt-0.5 font-mono text-[7px] tracking-[0.1em] uppercase">
        {label}
      </span>
    </div>
  );
}

function CheckGlyph() {
  return (
    <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
      <path
        d="M0.8 3 L2.4 4.6 L5.2 1.4"
        stroke="var(--color-marketing-accent-green-dark)"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function MatchedTile() {
  return (
    <div className="border-marketing-border bg-marketing-cream-light w-[170px] rounded-[10px] border px-3 py-2.5">
      <div className="border-marketing-badge-green-border bg-marketing-badge-green-bg text-marketing-accent-green-dark mb-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono text-[8.5px] tracking-[0.18em] uppercase">
        <span
          className="h-1 w-1 rounded-full"
          style={{ background: "var(--color-marketing-accent-green-dark)" }}
        />
        Matched
      </div>
      <p className="text-marketing-text text-[12px] leading-tight font-medium">
        Senior PMs
      </p>
      <p className="text-marketing-text-muted text-[10px] leading-tight">
        30 min interview
      </p>
    </div>
  );
}

function SubmissionTile() {
  return (
    <div className="border-marketing-border bg-marketing-cream-light w-[160px] rounded-[10px] border px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1.5">
        <div
          className="border-marketing-badge-green-border flex h-3.5 w-3.5 items-center justify-center rounded-full border"
          style={{
            background: "var(--color-marketing-badge-green-bg)",
          }}
        >
          <CheckGlyph />
        </div>
        <span className="text-marketing-text-secondary font-mono text-[8.5px] tracking-[0.18em] uppercase">
          Submission
        </span>
      </div>
      <p className="text-marketing-text text-[12px] leading-tight font-medium">
        Interview synced
      </p>
      <p className="text-marketing-text-muted text-[10px] leading-tight">
        review pending
      </p>
    </div>
  );
}

function PayoutTile() {
  return (
    <div className="border-marketing-border bg-marketing-cream-light w-[160px] rounded-[10px] border px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1.5">
        <div className="bg-marketing-blue flex h-4 w-4 items-center justify-center rounded-full">
          <span className="text-marketing-cream font-mono text-[9px] leading-none font-semibold">
            $
          </span>
        </div>
        <span className="text-marketing-text-secondary font-mono text-[8.5px] tracking-[0.18em] uppercase">
          Payout
        </span>
      </div>
      <p className="text-marketing-text text-[14px] leading-tight font-semibold">
        $420.00
      </p>
      <p className="text-marketing-text-muted text-[10px] leading-tight">
        sent today
      </p>
    </div>
  );
}
